import React, { Suspense, useContext } from 'react';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { AcademyProvider } from '../context/academyContext'; 
import PrivateRoute from '../context/PrivateRoute.js';
import DynamicLayout from './DynamicLayout';
import { withPlatformAwareness } from '../helpers/platformUtils';
import PlatformToolPage from '../pages/tools/PlatformToolPage';
import { PlatformContext } from '../context/PlatformContext';


// Critical components (load immediately) - INCLUDING STUDY PAGES
import HomePage from '../pages/index';
import AboutPage from '../pages/about';
import ContactPage from '../pages/contact/index.js';
import NotFoundPage from '../pages/404.js';

// Auth components
import LoginPage from '../pages/login.js';
import RegisterPage from '../pages/register.js';
import ForgotPassword from '../pages/forgotpassword.js';
import Profile from '../pages/profile.js';
import Account from '../pages/account.js';
import ProfileEdit from '../pages/profile-edit.js';
import DeleteAccountPage from '../pages/delete-account.js';

// Core pages
import Inbox from '../pages/inbox.js';
import Settings from '../pages/settings.js';
import CalendarPage from '../pages/calendar.js';
import Premium from '../pages/premium.js';
import Privacy from '../pages/privacy.js';
import CategoryPage from '../pages/categories.js';
import TagsPage from '../pages/tags/index.js';
import BirthChartPage from "../pages/birthchart.js";
import AscendantPage from "../pages/ascendant.js";
import MoonPhasePage from "../pages/moonphase.js";
import PlanetaryHoursPage from "../pages/planetaryhours.js";
import RetrogradesPage from "../pages/retrogrades.js";
import SolarReturnPage from "../pages/solarreturn.js";
import FAQ from '../pages/faq.js';
import SaturnReturnPage from "../pages/saturnreturn.js";
import LunarReturnPage from "../pages/lunarreturn.js";
import TransitsPage from "../pages/transits.js";

// Academy pages
import AcademyPage from '../academy/academy.js';
import AstrologyAcademyPage from '../academy/curriculum/astrology/astrology.js';
import TarotAcademyPage from '../academy/curriculum/tarot/tarot.js';
import RunesAcademyPage from '../academy/curriculum/runes/runes.js';
import KabbalahAcademyPage from '../academy/curriculum/kabbalah/kabbalah.js';
import NumerologyAcademyPage from '../academy/curriculum/numerology/numerology.js';

// Lesson pages
import TarotLessonPage from '../academy/lessons/tarotlesson.js';
import RuneLessonPage from '../academy/lessons/runelesson.js';
import AstrologyLessonPage from '../academy/lessons/astrologylesson.js';
import NumerologyLessonPage from '../academy/lessons/numerologylesson.js';

// Runes lessons
import Fehu from '../academy/lessons/runes/fehu.js';
import Uruz from '../academy/lessons/runes/uruz.js';

// STUDY PAGES - LAZY LOADED for faster builds and better performance
const StudyIndexPage = React.lazy(() => import('../pages/study/index.js'));
const Astrology = React.lazy(() => import('../pages/study/astrology.js'));
const Tarot = React.lazy(() => import('../pages/study/tarot.js'));
const Runes = React.lazy(() => import('../pages/study/runes/runes'));
const Kaballah = React.lazy(() => import('../pages/study/kaballah/kaballah'));

// Numerology study pages - LAZY LOADED
const Introduction = React.lazy(() => import('../pages/study/numerology/introduction'));
const Numerology = React.lazy(() => import('../pages/study/numerology/numerology'));
const NumerologyTreeOfLife = React.lazy(() => import('../pages/study/numerology/treeoflife'));
const NumerologyTen = React.lazy(() => import('../pages/study/numerology/ten'));
const NumerologyAstrology = React.lazy(() => import('../pages/study/numerology/astrology.js'));
const Gematria = React.lazy(() => import('../pages/study/numerology/gematria.js'));

// Tarot study pages - LAZY LOADED
const Symbology = React.lazy(() => import('../pages/study/tarot/symbology.js'));
const TarotNumerology = React.lazy(() => import('../pages/study/tarot/numerology.js'));
const MajorArcana = React.lazy(() => import('../pages/study/tarot/majorarcana.js'));
const MinorArcana = React.lazy(() => import('../pages/study/tarot/minorarcana.js'));
const TarotAndAstrology = React.lazy(() => import('../pages/study/tarot/tarotandastrology.js'));
const HistoryOfTarot = React.lazy(() => import('../pages/study/tarot/history.js'));
const SchoolsOfTarot = React.lazy(() => import('../pages/study/tarot/schoolsoftarot.js'));

// Kaballah study pages - LAZY LOADED
const FoundationOfKaballah = React.lazy(() => import('../pages/study/kaballah/foundation.js'));
const PrimarySources = React.lazy(() => import('../pages/study/kaballah/primarysources.js'));
const Sephiroth = React.lazy(() => import('../pages/study/kaballah/sephiroth.js'));
const Qliphoth = React.lazy(() => import('../pages/study/kaballah/qliphoth.js'));
const Angels = React.lazy(() => import('../pages/study/kaballah/angels.js'));

// Runes study pages - LAZY LOADED
const Structure = React.lazy(() => import('../pages/study/runes/structure'));
const FirstAett = React.lazy(() => import('../pages/study/runes/firstaett'));
const SecondAett = React.lazy(() => import('../pages/study/runes/secondaett'));
const ThirdAett = React.lazy(() => import('../pages/study/runes/thirdaett'));

// Astrology study pages - LAZY LOADED
const Planets = React.lazy(() => import('../pages/study/astrology/planets.js'));
const Dignities = React.lazy(() => import('../pages/study/astrology/dignities.js'));
const Horary = React.lazy(() => import('../pages/study/astrology/horary.js'));
const Houses = React.lazy(() => import('../pages/study/astrology/houses.js'));
const Interpretingachart = React.lazy(() => import('../pages/study/astrology/interpretingachart.js'));
const Synastry = React.lazy(() => import('../pages/study/astrology/synastry.js'));
const Transits = React.lazy(() => import('../pages/study/astrology/transits.js'));
const Thezodiac = React.lazy(() => import('../pages/study/astrology/thezodiac.js'));

// Tool pages - LAZY LOADED for better performance
const toolRoutes = [
  { path: '/tools/randomtarotcard', key: 'RandomTarotCard' },
  { path: '/tools/celticcross', key: 'CelticCross' },
  { path: '/tools/randomplayingcard', key: 'RandomPlayingCard', private: true },
  { path: '/tools/randomrune', key: 'RandomRune', private: true },
  { path: '/tools/randomcoinflipper', key: 'RandomCoinFlipper', private: true },
  { path: '/tools/randomnumber', key: 'RandomNumber', private: true },
  { path: '/tools/yesorno', key: 'YesNo', private: true },
  { path: '/tools/numerologycalculator', key: 'NumerologyCalculator', private: true },
  { path: '/tools/numerologyname', key: 'NameNumerology', private: true },
];

const learnToolRoutes = [
  { path: '/learn/tarot/randomtarotcard', key: 'RandomTarotCard', private: true },
  { path: '/learn/tarot/randomcoinflipper', key: 'RandomCoinFlipper', private: true },
  { path: '/learn/tarot/randomnumber', key: 'RandomNumber', private: true },
  { path: '/learn/tarot/randomrune', key: 'RandomRune', private: true },
  { path: '/learn/tarot/yesorno', key: 'YesNo', private: true },
];

const mobileToolRoutes = [
  { path: '/mobile/randomtarotcard', key: 'RandomTarotCard' },
  { path: '/mobile/randomplayingcard', key: 'RandomPlayingCard' },
  { path: '/mobile/randomrune', key: 'RandomRune' },
  { path: '/mobile/randomcoinflipper', key: 'RandomCoinFlipper' },
  { path: '/mobile/randomnumber', key: 'RandomNumber' },
  { path: '/mobile/yesorno', key: 'YesNo' },
  { path: '/mobile/numerologycalculator', key: 'NumerologyCalculator' },
];

// Mobile components
const MobileLogin = React.lazy(() => import('../mobile/pages/login.js'));
const MobileRegister = React.lazy(() => import('../mobile/pages/register.js'));
const MobileForgotPassword = React.lazy(() => import('../mobile/pages/forgotpassword.js'));

// Lazy loaded components
const Studytools = React.lazy(() => import('../pages/studytools.js'));
const StarMapPage = React.lazy(() => import('../pages/starmap.js'));
const MoonPhase = React.lazy(() => import('../pages/moonphase.js'));
const Quiz = React.lazy(() => import('../components/quizComponent.js'));
const Tools = React.lazy(() => import('../pages/tools.js'));
const Services = React.lazy(() => import('../pages/services/index.js'));
const Journal = React.lazy(() => import('../components/JournalDesktop.js'));
const Altar = React.lazy(() => import('../pages/altar.js'));
const Library = React.lazy(() => import('../pages/library.js'));
const BookPage = React.lazy(() => import('../components/library/bookPage.js'));
const ProfilePageComponent = React.lazy(() => import("./ProfilePageComponent.js"));

// Mobile pages - Lazy load these
const MobileIndex = React.lazy(() => import('../mobile/pages/index.js'));
const MobileAcademy = React.lazy(() => import('../mobile/pages/academy.js'));
const MobileProgress = React.lazy(() => import('../mobile/pages/progress.js'));
const MobileContact = React.lazy(() => import('../mobile/pages/contact.js'));
const MobileLibrary = React.lazy(() => import('../mobile/pages/library.js'));

const Dashboard = React.lazy(() => import('../mobile/pages/dashboard.js'));
const ProfileMobilePage = React.lazy(() => import('../mobile/pages/profile'));
const AccountMobilePage = React.lazy(() => import('../mobile/pages/account'));
const DeleteAccountMobilePage = React.lazy(() => import('../mobile/pages/delete-account'));

// Mobile random tools - Lazy load these
const MobileNameNumerology = React.lazy(() => import('../mobile/pages/numerologyname.js'));
const MobileJournal = React.lazy(() => import('../mobile/pages/journal.js'));
const MobileCalendar = React.lazy(() => import('../calendar/mobile/CalendarMobilePage.js'));

// Tools and utilities - Lazy load these (less frequently used)

// TAROT CARD PAGES - Lazy load these (they're individual cards, not main study pages)
const MajorArcanaCards = {
  TheFool: React.lazy(() => import('../pages/study/tarot/majorarcana/thefool.js')),
  TheMagician: React.lazy(() => import('../pages/study/tarot/majorarcana/themagician.js')),
  TheHighPriestess: React.lazy(() => import('../pages/study/tarot/majorarcana/thehighpriestess.js')),
  TheEmpress: React.lazy(() => import('../pages/study/tarot/majorarcana/theempress.js')),
  TheEmperor: React.lazy(() => import('../pages/study/tarot/majorarcana/theemperor.js')),
  TheHierophant: React.lazy(() => import('../pages/study/tarot/majorarcana/thehierophant.js')),
  TheLovers: React.lazy(() => import('../pages/study/tarot/majorarcana/thelovers.js')),
  TheChariot: React.lazy(() => import('../pages/study/tarot/majorarcana/thechariot.js')),
  Strength: React.lazy(() => import('../pages/study/tarot/majorarcana/strength.js')),
  TheHermit: React.lazy(() => import('../pages/study/tarot/majorarcana/thehermit.js')),
  WheelOfFortune: React.lazy(() => import('../pages/study/tarot/majorarcana/wheeloffortune.js')),
  Justice: React.lazy(() => import('../pages/study/tarot/majorarcana/justice.js')),
  TheHangedMan: React.lazy(() => import('../pages/study/tarot/majorarcana/thehangedman.js')),
  Death: React.lazy(() => import('../pages/study/tarot/majorarcana/death.js')),
  Temperance: React.lazy(() => import('../pages/study/tarot/majorarcana/temperance.js')),
  TheDevil: React.lazy(() => import('../pages/study/tarot/majorarcana/thedevil.js')),
  TheTower: React.lazy(() => import('../pages/study/tarot/majorarcana/thetower.js')),
  TheStar: React.lazy(() => import('../pages/study/tarot/majorarcana/thestar.js')),
  TheMoon: React.lazy(() => import('../pages/study/tarot/majorarcana/themoon.js')),
  TheSun: React.lazy(() => import('../pages/study/tarot/majorarcana/thesun.js')),
  Judgement: React.lazy(() => import('../pages/study/tarot/majorarcana/judgement.js')),
  TheWorld: React.lazy(() => import('../pages/study/tarot/majorarcana/theworld.js')),
};

const MinorArcanaCards = {
  // Wands
  AceOfWands: React.lazy(() => import('../pages/study/tarot/minorarcana/aceofwands.js')),
  TwoOfWands: React.lazy(() => import('../pages/study/tarot/minorarcana/twoofwands.js')),
  ThreeOfWands: React.lazy(() => import('../pages/study/tarot/minorarcana/threeofwands.js')),
  FourOfWands: React.lazy(() => import('../pages/study/tarot/minorarcana/fourofwands.js')),
  FiveOfWands: React.lazy(() => import('../pages/study/tarot/minorarcana/fiveofwands.js')),
  SixOfWands: React.lazy(() => import('../pages/study/tarot/minorarcana/sixofwands.js')),
  SevenOfWands: React.lazy(() => import('../pages/study/tarot/minorarcana/sevenofwands.js')),
  EightOfWands: React.lazy(() => import('../pages/study/tarot/minorarcana/eightofwands.js')),
  NineOfWands: React.lazy(() => import('../pages/study/tarot/minorarcana/nineofwands.js')),
  TenOfWands: React.lazy(() => import('../pages/study/tarot/minorarcana/tenofwands.js')),
  PageOfWands: React.lazy(() => import('../pages/study/tarot/minorarcana/pageofwands.js')),
  KnightOfWands: React.lazy(() => import('../pages/study/tarot/minorarcana/knightofwands.js')),
  QueenOfWands: React.lazy(() => import('../pages/study/tarot/minorarcana/queenofwands.js')),
  KingOfWands: React.lazy(() => import('../pages/study/tarot/minorarcana/kingofwands.js')),
  
  // Cups
  AceOfCups: React.lazy(() => import('../pages/study/tarot/minorarcana/aceofcups.js')),
  TwoOfCups: React.lazy(() => import('../pages/study/tarot/minorarcana/twoofcups.js')),
  ThreeOfCups: React.lazy(() => import('../pages/study/tarot/minorarcana/threeofcups.js')),
  FourOfCups: React.lazy(() => import('../pages/study/tarot/minorarcana/fourofcups.js')),
  FiveOfCups: React.lazy(() => import('../pages/study/tarot/minorarcana/fiveofcups.js')),
  SixOfCups: React.lazy(() => import('../pages/study/tarot/minorarcana/sixofcups.js')),
  SevenOfCups: React.lazy(() => import('../pages/study/tarot/minorarcana/sevenofcups.js')),
  EightOfCups: React.lazy(() => import('../pages/study/tarot/minorarcana/eightofcups.js')),
  NineOfCups: React.lazy(() => import('../pages/study/tarot/minorarcana/nineofcups.js')),
  TenOfCups: React.lazy(() => import('../pages/study/tarot/minorarcana/tenofcups.js')),
  PageOfCups: React.lazy(() => import('../pages/study/tarot/minorarcana/pageofcups.js')),
  KnightOfCups: React.lazy(() => import('../pages/study/tarot/minorarcana/knightofcups.js')),
  QueenOfCups: React.lazy(() => import('../pages/study/tarot/minorarcana/queenofcups.js')),
  KingOfCups: React.lazy(() => import('../pages/study/tarot/minorarcana/kingofcups.js')),
  
  // Swords
  AceOfSwords: React.lazy(() => import('../pages/study/tarot/minorarcana/aceofswords.js')),
  TwoOfSwords: React.lazy(() => import('../pages/study/tarot/minorarcana/twoofswords.js')),
  ThreeOfSwords: React.lazy(() => import('../pages/study/tarot/minorarcana/threeofswords.js')),
  FourOfSwords: React.lazy(() => import('../pages/study/tarot/minorarcana/fourofswords.js')),
  FiveOfSwords: React.lazy(() => import('../pages/study/tarot/minorarcana/fiveofswords.js')),
  SixOfSwords: React.lazy(() => import('../pages/study/tarot/minorarcana/sixofswords.js')),
  SevenOfSwords: React.lazy(() => import('../pages/study/tarot/minorarcana/sevenofswords.js')),
  EightOfSwords: React.lazy(() => import('../pages/study/tarot/minorarcana/eightofswords.js')),
  NineOfSwords: React.lazy(() => import('../pages/study/tarot/minorarcana/nineofswords.js')),
  TenOfSwords: React.lazy(() => import('../pages/study/tarot/minorarcana/tenofswords.js')),
  PageOfSwords: React.lazy(() => import('../pages/study/tarot/minorarcana/pageofswords.js')),
  KnightOfSwords: React.lazy(() => import('../pages/study/tarot/minorarcana/knightofswords.js')),
  QueenOfSwords: React.lazy(() => import('../pages/study/tarot/minorarcana/queenofswords.js')),
  KingOfSwords: React.lazy(() => import('../pages/study/tarot/minorarcana/kingofswords.js')),
  
  // Pentacles
  AceOfPentacles: React.lazy(() => import('../pages/study/tarot/minorarcana/aceofpentacles.js')),
  TwoOfPentacles: React.lazy(() => import('../pages/study/tarot/minorarcana/twoofpentacles.js')),
  ThreeOfPentacles: React.lazy(() => import('../pages/study/tarot/minorarcana/threeofpentacles.js')),
  FourOfPentacles: React.lazy(() => import('../pages/study/tarot/minorarcana/fourofpentacles.js')),
  FiveOfPentacles: React.lazy(() => import('../pages/study/tarot/minorarcana/fiveofpentacles.js')),
  SixOfPentacles: React.lazy(() => import('../pages/study/tarot/minorarcana/sixofpentacles.js')),
  SevenOfPentacles: React.lazy(() => import('../pages/study/tarot/minorarcana/sevenofpentacles.js')),
  EightOfPentacles: React.lazy(() => import('../pages/study/tarot/minorarcana/eightofpentacles.js')),
  NineOfPentacles: React.lazy(() => import('../pages/study/tarot/minorarcana/nineofpentacles.js')),
  TenOfPentacles: React.lazy(() => import('../pages/study/tarot/minorarcana/tenofpentacles.js')),
  PageOfPentacles: React.lazy(() => import('../pages/study/tarot/minorarcana/pageofpentacles.js')),
  KnightOfPentacles: React.lazy(() => import('../pages/study/tarot/minorarcana/knightofpentacles.js')),
  QueenOfPentacles: React.lazy(() => import('../pages/study/tarot/minorarcana/queenofpentacles.js')),
  KingOfPentacles: React.lazy(() => import('../pages/study/tarot/minorarcana/kingofpentacles.js')),
};



// Loading component for Suspense
const LoadingSpinner = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '50vh',
    color: 'rgba(255, 215, 0, 0.6)',
    fontSize: '1rem'
  }}>
    <div style={{
      width: '30px',
      height: '30px',
      border: '3px solid rgba(255, 215, 0, 0.3)',
      borderTop: '3px solid rgba(255, 215, 0, 0.8)',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }}></div>
  </div>
);

const AuthPlatformRoute = ({ desktopElement, mobilePath }) => {
  const { platform, loading } = useContext(PlatformContext);

  if (loading) {
    return <LoadingSpinner />;
  }

  const isMobile = platform === 'mobileApp' || platform === 'mobileWeb';
  return isMobile ? <Navigate to={mobilePath} replace /> : desktopElement;
};

// Dynamic components using platform detection
const JournalComponent = withPlatformAwareness('Journal');
const CalendarComponent = withPlatformAwareness('Calendar');

const AppRoutes = () => {
  const navigate = useNavigate();
  
  const handleLevelChange = (level) => {
    navigate(`/academy/${level}`);
  };

  const handleRunesLevelChange = (level) => {
    navigate(`/academy/runes/${level}`);
  };

  return (
    <Routes>
      {/* Critical routes - load immediately */}
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route
        path="/login"
        element={<AuthPlatformRoute desktopElement={<LoginPage />} mobilePath="/mobile/login" />}
      />
      <Route path="/mobile/login" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MobileLogin />
        </Suspense>
      } />
      <Route
        path="/register"
        element={<AuthPlatformRoute desktopElement={<RegisterPage />} mobilePath="/mobile/register" />}
      />
      <Route path="/mobile/register" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MobileRegister />
        </Suspense>
      } />
      <Route path="/forgotpassword" element={<ForgotPassword />} />
      <Route path="/mobile/forgotpassword" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MobileForgotPassword />
        </Suspense>
      } />
      <Route path="*" element={<NotFoundPage />} />

      {/* Core pages */}
      <Route path="/starmap" element={
        <Suspense fallback={<LoadingSpinner />}>
          <StarMapPage />
        </Suspense>
      } />
      <Route path="/dashboard" element={
        <Suspense fallback={<LoadingSpinner />}>
          <Dashboard />
        </Suspense>
      } />
      <Route path="/messages" element={<Inbox />} />
      <Route path="/tools" element={
        <Suspense fallback={<LoadingSpinner />}>
          <Tools />
        </Suspense>
      } />
      <Route path="/altar" element={
        <Suspense fallback={<LoadingSpinner />}>
          <Altar />
        </Suspense>
      } />
      <Route path="/settings" element={<Settings />} />
      <Route path="/premium" element={<Premium />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/categories" element={<CategoryPage />} />
      <Route path="/tags" element={<TagsPage />} />
      <Route path="/study/quiz" element={
        <PrivateRoute element={
          <Suspense fallback={<LoadingSpinner />}>
            <Quiz />
          </Suspense>
        } />
      } />
      <Route path="/moonphase" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MoonPhase />
        </Suspense>
      } />
      <Route path="/birthchart" element={<BirthChartPage />} />
      <Route path="/ascendant" element={<AscendantPage />} />
      <Route path="/planetaryhours" element={<PlanetaryHoursPage />} />
      <Route path="/retrogrades" element={<RetrogradesPage />} />
      <Route path="/solarreturn" element={<SolarReturnPage />} />
      <Route path="/saturnreturn" element={<SaturnReturnPage />} />
      <Route path="/lunarreturn" element={<LunarReturnPage />} />
      <Route path="/transits" element={<TransitsPage />} />
      <Route path="/journal" element={
        <PrivateRoute element={
          <Suspense fallback={<LoadingSpinner />}>
            <JournalComponent />
          </Suspense>
        } />
      } />
      <Route path="/calendar" element={
        <PrivateRoute element={
          <Suspense fallback={<LoadingSpinner />}>
            <CalendarComponent />
          </Suspense>
        } />
      } />
      <Route path="/account" element={<PrivateRoute element={<Account />} />} />
      <Route path="/profile" element={<PrivateRoute element={<Profile />} />} />
      <Route path="/profile-edit" element={<PrivateRoute element={<ProfileEdit />} />} />
      <Route path="/delete-account" element={<PrivateRoute element={<DeleteAccountPage />} />} />
      <Route path="/profile/:username" element={
        <Suspense fallback={<LoadingSpinner />}>
          <ProfilePageComponent />
        </Suspense>
      } />
      <Route path="/services" element={
        <Suspense fallback={<LoadingSpinner />}>
          <Services />
        </Suspense>
      } />
      <Route path="/studytools" element={
        <PrivateRoute element={
          <Suspense fallback={<LoadingSpinner />}>
            <Studytools />
          </Suspense>
        } />
      } />
      <Route path="/library" element={
        <Suspense fallback={<LoadingSpinner />}>
          <Library />
        </Suspense>
      } />
      <Route path="/books/:bookname" element={
        <Suspense fallback={<LoadingSpinner />}>
          <BookPage />
        </Suspense>
      } />

      {/* Academy routes */}
      <Route path="/academy" element={<PrivateRoute element={<AcademyPage />} />} />
      <Route path="/mobile/academy" element={
        <PrivateRoute element={
          <Suspense fallback={<LoadingSpinner />}>
            <MobileAcademy />
          </Suspense>
        } />
      } />
      <Route path="/progress" element={
        <PrivateRoute element={
          <Suspense fallback={<LoadingSpinner />}>
            <MobileProgress />
          </Suspense>
        } />
      } />
      <Route path="/mobile/contact" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MobileContact />
        </Suspense>
      } />
      <Route path="/mobile/library" element={
        <PrivateRoute element={
          <Suspense fallback={<LoadingSpinner />}>
            <MobileLibrary />
          </Suspense>
        } />
      } />
      <Route path="/mobile/journal" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MobileJournal />
        </Suspense>
      } />
      <Route path="/mobile/calendar" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MobileCalendar />
        </Suspense>
      } />



      <Route path="/academy/kabbalah" element={
        <PrivateRoute element={
          <AcademyProvider>
            <KabbalahAcademyPage />
          </AcademyProvider>
        } />
      } />

      <Route path="/academy/astrology" element={
        <PrivateRoute element={
          <AcademyProvider>
            <AstrologyAcademyPage />
          </AcademyProvider>
        } />
      } />

      <Route path="/academy/tarot/" element={
        <PrivateRoute element={
          <AcademyProvider>
            <TarotAcademyPage />
          </AcademyProvider>
        } />
      } />

      <Route path="/academy/numerology/" element={
        <PrivateRoute element={
          <AcademyProvider>
            <NumerologyAcademyPage />
          </AcademyProvider>
        } />
      } />

      <Route path="/academy/runes/" element={
        <PrivateRoute element={
          <AcademyProvider>
            <RunesAcademyPage />
          </AcademyProvider>
        } />
      } />

      <Route path="/academy/runes/:level/lesson" element={
        <PrivateRoute element={
          <AcademyProvider>
            <RuneLessonPage />
          </AcademyProvider>
        } />
      } />

      <Route path="/academy/astrology/:level/lesson" element={
        <PrivateRoute element={
          <AcademyProvider>
            <AstrologyLessonPage />
          </AcademyProvider>
        } />
      } />

      <Route path="/academy/numerology/:level/lesson" element={
        <PrivateRoute element={
          <AcademyProvider>
            <NumerologyLessonPage />
          </AcademyProvider>
        } />
      } />

      <Route path="/academy/tarot/:level/lesson" element={
        <PrivateRoute element={
          <AcademyProvider>
            <TarotLessonPage />
          </AcademyProvider>
        } />
      } />

      <Route path="/academy/runes/fehu" element={<Fehu />} />
      <Route path="/academy/runes/uruz" element={<Uruz />} />

      {/* Mobile routes */}
      <Route path="/mobile/index" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MobileIndex />
        </Suspense>
      } />
      <Route path="/mobile/profile" element={
        <PrivateRoute element={
          <Suspense fallback={<LoadingSpinner />}>
            <ProfileMobilePage />
          </Suspense>
        } />
      } />
      <Route path="/mobile/account" element={
        <Suspense fallback={<LoadingSpinner />}>
          <AccountMobilePage />
        </Suspense>
      } />
      <Route path="/mobile/delete-account" element={
        <Suspense fallback={<LoadingSpinner />}>
          <DeleteAccountMobilePage />
          
        </Suspense>
      } />

      {/* Tool routes - driven by config */}
      {toolRoutes.map(({ path, key, private: isPrivate }) => {
        const element = (
          <Suspense fallback={<LoadingSpinner />}>
            <PlatformToolPage componentKey={key} />
          </Suspense>
        );
        return (
          <Route
            key={path}
            path={path}
            element={isPrivate ? <PrivateRoute element={element} /> : element}
          />
        );
      })}

      {/* STUDY ROUTES - LAZY LOADED with Suspense for faster builds */}
      <Route path="/study" element={
        <Suspense fallback={<LoadingSpinner />}>
          <StudyIndexPage />
        </Suspense>
      } />
      <Route path="/study/astrology" element={
        <Suspense fallback={<LoadingSpinner />}>
          <Astrology />
        </Suspense>
      } />
      {learnToolRoutes.map(({ path, key, private: isPrivate }) => {
        const element = (
          <Suspense fallback={<LoadingSpinner />}>
            <PlatformToolPage componentKey={key} />
          </Suspense>
        );
        return (
          <Route
            key={path}
            path={path}
            element={isPrivate ? <PrivateRoute element={element} /> : element}
          />
        );
      })}

      {/* Numerology study routes - LAZY LOADED */}
      <Route path="/study/numerology" element={<Suspense fallback={<LoadingSpinner />}><Numerology /></Suspense>} />
      <Route path="/study/numerology/introduction" element={<Suspense fallback={<LoadingSpinner />}><Introduction /></Suspense>} />
      <Route path="/study/numerology/treeoflife" element={<Suspense fallback={<LoadingSpinner />}><NumerologyTreeOfLife /></Suspense>} />
      <Route path="/study/numerology/ten" element={<Suspense fallback={<LoadingSpinner />}><NumerologyTen /></Suspense>} />
      <Route path="/study/numerology/gematria" element={<Suspense fallback={<LoadingSpinner />}><Gematria /></Suspense>} />
      <Route path="/study/numerology/astrology" element={<Suspense fallback={<LoadingSpinner />}><NumerologyAstrology /></Suspense>} />

      {/* Kaballah study routes - LAZY LOADED */}
      <Route path="/study/kaballah" element={<Suspense fallback={<LoadingSpinner />}><Kaballah /></Suspense>} />
      <Route path="/study/kaballah/foundation" element={<Suspense fallback={<LoadingSpinner />}><FoundationOfKaballah /></Suspense>} />
      <Route path="/study/kaballah/primarysources" element={<Suspense fallback={<LoadingSpinner />}><PrimarySources /></Suspense>} />
      <Route path="/study/kaballah/sephiroth" element={<Suspense fallback={<LoadingSpinner />}><Sephiroth /></Suspense>} />
      <Route path="/study/kaballah/qliphoth" element={<Suspense fallback={<LoadingSpinner />}><Qliphoth /></Suspense>} />
      <Route path="/study/kaballah/angels" element={<Suspense fallback={<LoadingSpinner />}><Angels /></Suspense>} />

      {/* Runes study routes - LAZY LOADED */}
      <Route path="/study/runes" element={<Suspense fallback={<LoadingSpinner />}><Runes /></Suspense>} />
      <Route path="/study/runes/structure" element={<Suspense fallback={<LoadingSpinner />}><Structure /></Suspense>} />
      <Route path="/study/runes/firstaett" element={<Suspense fallback={<LoadingSpinner />}><FirstAett /></Suspense>} />
      <Route path="/study/runes/secondaett" element={<Suspense fallback={<LoadingSpinner />}><SecondAett /></Suspense>} />
      <Route path="/study/runes/thirdaett" element={<Suspense fallback={<LoadingSpinner />}><ThirdAett /></Suspense>} />

      {/* Tarot study routes - LAZY LOADED */}
      <Route path="/study/tarot" element={<Suspense fallback={<LoadingSpinner />}><Tarot /></Suspense>} />
      <Route path="/study/tarot/minorarcana" element={<Suspense fallback={<LoadingSpinner />}><MinorArcana /></Suspense>} />
      <Route path="/study/tarot/symbology" element={<Suspense fallback={<LoadingSpinner />}><Symbology /></Suspense>} />
      <Route path="/study/tarot/numerology" element={<Suspense fallback={<LoadingSpinner />}><TarotNumerology /></Suspense>} />
      <Route path="/study/tarot/tarotandastrology" element={<Suspense fallback={<LoadingSpinner />}><TarotAndAstrology /></Suspense>} />
      <Route path="/study/tarot/history" element={<Suspense fallback={<LoadingSpinner />}><HistoryOfTarot /></Suspense>} />
      <Route path="/study/tarot/schoolsoftarot" element={<Suspense fallback={<LoadingSpinner />}><SchoolsOfTarot /></Suspense>} />
      <Route path="/study/tarot/majorarcana" element={<Suspense fallback={<LoadingSpinner />}><MajorArcana /></Suspense>} />

      {/* Astrology study routes - LAZY LOADED */}
      <Route path="/study/astrology/planets" element={<Suspense fallback={<LoadingSpinner />}><Planets /></Suspense>} />
      <Route path="/study/astrology/thezodiac" element={<Suspense fallback={<LoadingSpinner />}><Thezodiac /></Suspense>} />
      <Route path="/study/astrology/dignities" element={<Suspense fallback={<LoadingSpinner />}><Dignities /></Suspense>} />
      <Route path="/study/astrology/transits" element={<Suspense fallback={<LoadingSpinner />}><Transits /></Suspense>} />
      <Route path="/study/astrology/houses" element={<Suspense fallback={<LoadingSpinner />}><Houses /></Suspense>} />
      <Route path="/study/astrology/interpretingachart" element={<Suspense fallback={<LoadingSpinner />}><Interpretingachart /></Suspense>} />
      <Route path="/study/astrology/horary" element={<Suspense fallback={<LoadingSpinner />}><Horary /></Suspense>} />
      <Route path="/study/astrology/synastry" element={<Suspense fallback={<LoadingSpinner />}><Synastry /></Suspense>} />

      {/* Individual Tarot Card routes - Lazy load these (they're detailed pages) */}
      {/* Major Arcana routes */}
      <Route path="/study/tarot/majorarcana/thefool" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.TheFool />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/themagician" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.TheMagician />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/thehighpriestess" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.TheHighPriestess />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/theempress" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.TheEmpress />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/theemperor" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.TheEmperor />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/thehierophant" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.TheHierophant />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/thelovers" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.TheLovers />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/thechariot" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.TheChariot />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/strength" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.Strength />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/thehermit" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.TheHermit />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/wheeloffortune" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.WheelOfFortune />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/justice" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.Justice />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/thehangedman" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.TheHangedMan />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/death" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.Death />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/temperance" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.Temperance />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/thedevil" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.TheDevil />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/thetower" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.TheTower />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/thestar" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.TheStar />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/themoon" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.TheMoon />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/thesun" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.TheSun />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/judgement" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.Judgement />
        </Suspense>
      } />
      <Route path="/study/tarot/majorarcana/theworld" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MajorArcanaCards.TheWorld />
        </Suspense>
      } />

      {/* Minor Arcana - Wands routes */}
      <Route path="/study/tarot/minorarcana/aceofwands" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.AceOfWands />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/twoofwands" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.TwoOfWands />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/threeofwands" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.ThreeOfWands />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/fourofwands" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.FourOfWands />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/fiveofwands" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.FiveOfWands />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/sixofwands" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.SixOfWands />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/sevenofwands" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.SevenOfWands />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/eightofwands" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.EightOfWands />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/nineofwands" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.NineOfWands />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/tenofwands" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.TenOfWands />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/pageofwands" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.PageOfWands />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/knightofwands" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.KnightOfWands />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/queenofwands" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.QueenOfWands />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/kingofwands" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.KingOfWands />
        </Suspense>
      } />

      {/* Minor Arcana - Cups routes */}
      <Route path="/study/tarot/minorarcana/aceofcups" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.AceOfCups />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/twoofcups" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.TwoOfCups />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/threeofcups" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.ThreeOfCups />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/fourofcups" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.FourOfCups />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/fiveofcups" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.FiveOfCups />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/sixofcups" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.SixOfCups />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/sevenofcups" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.SevenOfCups />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/eightofcups" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.EightOfCups />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/nineofcups" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.NineOfCups />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/tenofcups" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.TenOfCups />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/pageofcups" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.PageOfCups />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/knightofcups" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.KnightOfCups />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/queenofcups" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.QueenOfCups />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/kingofcups" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.KingOfCups />
        </Suspense>
      } />

      {/* Minor Arcana - Swords routes */}
      <Route path="/study/tarot/minorarcana/aceofswords" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.AceOfSwords />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/twoofswords" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.TwoOfSwords />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/threeofswords" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.ThreeOfSwords />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/fourofswords" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.FourOfSwords />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/fiveofswords" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.FiveOfSwords />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/sixofswords" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.SixOfSwords />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/sevenofswords" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.SevenOfSwords />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/eightofswords" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.EightOfSwords />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/nineofswords" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.NineOfSwords />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/tenofswords" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.TenOfSwords />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/pageofswords" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.PageOfSwords />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/knightofswords" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.KnightOfSwords />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/queenofswords" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.QueenOfSwords />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/kingofswords" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.KingOfSwords />
        </Suspense>
      } />

      {/* Minor Arcana - Pentacles routes */}
      <Route path="/study/tarot/minorarcana/aceofpentacles" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.AceOfPentacles />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/twoofpentacles" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.TwoOfPentacles />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/threeofpentacles" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.ThreeOfPentacles />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/fourofpentacles" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.FourOfPentacles />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/fiveofpentacles" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.FiveOfPentacles />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/sixofpentacles" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.SixOfPentacles />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/sevenofpentacles" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.SevenOfPentacles />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/eightofpentacles" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.EightOfPentacles />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/nineofpentacles" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.NineOfPentacles />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/tenofpentacles" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.TenOfPentacles />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/pageofpentacles" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.PageOfPentacles />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/knightofpentacles" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.KnightOfPentacles />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/queenofpentacles" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.QueenOfPentacles />
        </Suspense>
      } />
      <Route path="/study/tarot/minorarcana/kingofpentacles" element={
        <Suspense fallback={<LoadingSpinner />}>
          <MinorArcanaCards.KingOfPentacles />
        </Suspense>
      } />

      {/* Mobile routes for random tools */}
      {mobileToolRoutes.map(({ path, key }) => {
        const element = (
          <Suspense fallback={<LoadingSpinner />}>
            <PlatformToolPage componentKey={key} withWallpaper={false} />
          </Suspense>
        );
        return (
          <Route
            key={path}
            path={path}
            element={<PrivateRoute element={element} />}
          />
        );
      })}

    </Routes>
  );
};

export default AppRoutes;
